"use client"

import { useEffect, useRef, useState } from "react"
import "./types"
import "./model-viewer.css"

interface ModelViewerProps {
  src: string
  alt?: string
  className?: string
  style?: React.CSSProperties
  onLoad?: () => void
  onError?: (error: Error) => void
}

export function ModelViewer({
  src,
  alt,
  className = "",
  style,
  onLoad,
  onError,
}: ModelViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    let mounted = true

    const loadModelViewer = async () => {
      try {
        // Dynamically import model-viewer to ensure it loads on the client side
        await import("@google/model-viewer")
        if (mounted) {
          setIsLoaded(true)
          onLoad?.()
        }
      } catch (error) {
        if (mounted) {
          setHasError(true)
          onError?.(error as Error)
        }
      }
    }

    loadModelViewer()

    return () => {
      mounted = false
    }
  }, [onLoad, onError])

  const handleError = (event: Event) => {
    setHasError(true)
    onError?.(new Error("Failed to load 3D model"))
  }

  const handleModelLoad = () => {
    onLoad?.()
    // Enable animations immediately on load
    const modelViewer = containerRef.current?.querySelector(
      "model-viewer",
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    ) as any
    if (modelViewer) {
      // Play all animations
      const availableAnimations = modelViewer.availableAnimations
      if (availableAnimations && availableAnimations.length > 0) {
        modelViewer.animationName = availableAnimations[0] // Play first animation
        modelViewer.play()
      }
    }
  }

  if (hasError) {
    return (
      <div
        className={`flex items-center justify-center bg-muted text-muted-foreground ${className}`}
        style={style}
      >
        <div className="text-center">
          <div className="text-sm">Failed to load 3D model</div>
        </div>
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div
        className={`flex animate-pulse items-center justify-center bg-muted text-muted-foreground ${className}`}
        style={style}
      >
        <div className="text-center">
          <div className="text-sm">Loading 3D model...</div>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative h-full w-full">
      <model-viewer
        src={src}
        ios-src={src}
        alt={alt}
        camera-controls={true}
        auto-rotate={true}
        auto-rotate-delay="0"
        rotation-per-second="30deg"
        autoplay={true}
        ar
        loading="lazy"
        reveal="auto"
        interaction-prompt="none"
        interaction-prompt-threshold="0"
        camera-target="auto auto auto"
        camera-orbit="0deg 75deg auto"
        field-of-view="30deg"
        min-camera-orbit="auto auto 0.1m"
        max-camera-orbit="auto auto 4m"
        className={`${className} h-full w-full`}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
          backgroundColor: "transparent",
          pointerEvents: "auto",
          ...style,
        }}
        onLoad={handleModelLoad}
        onError={handleError}
      />
    </div>
  )
}
